// ============================================================================
// frontend/src/components/tenant/AgentsSection.tsx - NOUVEAU COMPOSANT
// ============================================================================

import React, { useEffect, useState, useContext } from 'react';
import { ChainlitContext } from '@chainlit/react-client';
import { TenantFormData, Agent, SelectedAgent } from '@/types/tenant';
import { Loader } from '@/components/Loader';

interface AgentsSectionProps {
  formData: TenantFormData;
  setFormData: (data: TenantFormData) => void;
}

export const AgentsSection: React.FC<AgentsSectionProps> = ({
  formData,
  setFormData
}) => {
  const apiClient = useContext(ChainlitContext);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        apiClient.buildEndpoint('/api/agents/?is_active=true'),
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des agents');
      }

      const data = await response.json();
      setAgents(data.agents || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isAgentSelected = (agentId: string): boolean => {
    return formData.selected_agents.some(sa => sa.agent_id === agentId);
  };

  const getSelectedAgent = (agentId: string): SelectedAgent | undefined => {
    return formData.selected_agents.find(sa => sa.agent_id === agentId);
  };

  const toggleAgent = (agent: Agent) => {
    const isSelected = isAgentSelected(agent.id);
    
    if (isSelected) {
      // Retirer l'agent
      setFormData({
        ...formData,
        selected_agents: formData.selected_agents.filter(sa => sa.agent_id !== agent.id)
      });
    } else {
      // Ajouter l'agent
      const newAgent: SelectedAgent = {
        agent_id: agent.id,
        is_enabled: true,
        is_default: agent.is_default,
        display_order: formData.selected_agents.length
      };
      setFormData({
        ...formData,
        selected_agents: [...formData.selected_agents, newAgent]
      });
    }
  };

  const toggleAgentEnabled = (agentId: string) => {
    setFormData({
      ...formData,
      selected_agents: formData.selected_agents.map(sa =>
        sa.agent_id === agentId ? { ...sa, is_enabled: !sa.is_enabled } : sa
      )
    });
  };

  const setAgentAsDefault = (agentId: string) => {
    setFormData({
      ...formData,
      selected_agents: formData.selected_agents.map(sa => ({
        ...sa,
        is_default: sa.agent_id === agentId
      }))
    });
  };

  const selectAllAgents = () => {
    const allAgents: SelectedAgent[] = agents.map((agent, index) => ({
      agent_id: agent.id,
      is_enabled: true,
      is_default: agent.is_default,
      display_order: index
    }));
    setFormData({ ...formData, selected_agents: allAgents });
  };

  const deselectAllAgents = () => {
    setFormData({ ...formData, selected_agents: [] });
  };

  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-xl font-semibold border-b pb-3 mb-4">🤖 Agents disponibles</h2>
        <div className="flex items-center justify-center py-12">
          <Loader className="!size-8" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6 space-y-6">
      <div className="border-b pb-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold">🤖 Agents disponibles</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {formData.selected_agents.length} / {agents.length} sélectionné(s)
            </span>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Sélectionnez les agents juridiques qui seront disponibles pour ce tenant
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Actions rapides */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={selectAllAgents}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-input hover:bg-accent text-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Tout sélectionner
        </button>
        <button
          type="button"
          onClick={deselectAllAgents}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-input hover:bg-accent text-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Tout désélectionner
        </button>
      </div>

      {/* Recherche */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Rechercher un agent..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm"
        />
      </div>

      {/* Liste des agents */}
      {filteredAgents.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">
          {searchQuery ? 'Aucun agent trouvé' : 'Aucun agent disponible'}
        </p>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {filteredAgents.map((agent) => {
            const selectedAgent = getSelectedAgent(agent.id);
            const isSelected = !!selectedAgent;

            return (
              <div
                key={agent.id}
                className={`p-4 rounded-lg border transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-accent'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox de sélection */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleAgent(agent)}
                    className="mt-1 rounded"
                  />

                  {/* Info agent */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{agent.icon_emoji}</span>
                      <div className="flex-1">
                        <h3 className="font-semibold">{agent.name}</h3>
                        <p className="text-xs text-muted-foreground font-mono">{agent.slug}</p>
                      </div>
                      <div className="flex gap-1">
                        {agent.agent_type === 'langgraph_hitl' && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                            🔄 HITL
                          </span>
                        )}
                        {agent.is_default && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            Défaut
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {agent.description}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {agent.model}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {agent.tools_count} tools
                      </span>
                    </div>

                    {/* Options si sélectionné */}
                    {isSelected && selectedAgent && (
                      <div className="mt-3 pt-3 border-t flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedAgent.is_enabled}
                            onChange={() => toggleAgentEnabled(agent.id)}
                            className="rounded"
                          />
                          <span className="text-sm">Activé</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="default_agent"
                            checked={selectedAgent.is_default}
                            onChange={() => setAgentAsDefault(agent.id)}
                            className="rounded-full"
                          />
                          <span className="text-sm">Par défaut</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Warning si aucun agent sélectionné */}
      {formData.selected_agents.length === 0 && (
        <div className="p-4 rounded-md bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            ⚠️ <strong>Attention :</strong> Aucun agent sélectionné. Le tenant n'aura accès à aucun agent juridique.
          </p>
        </div>
      )}

      {/* Info */}
      <div className="p-3 rounded-md bg-muted/50 text-sm text-muted-foreground">
        ℹ️ Les agents sélectionnés pourront être configurés individuellement après la création du tenant.
      </div>
    </div>
  );
};
